/* ============================================================
   Konko — Header: mega menú desktop (flyout) + drawer por niveles (mobile)
   Aislado, sin dependencias externas. No reemplaza la apertura/cierre de
   #NavDrawer ni #CartDrawer (queda a cargo del theme.js existente): solo
   añade comportamiento nuevo sobre marcado nuevo.
   ============================================================ */
(function () {
  'use strict';

  var DESKTOP_QUERY = '(min-width: 990px)';

  function qsa(sel, ctx) {
    return Array.prototype.slice.call((ctx || document).querySelectorAll(sel));
  }
  function isDesktop() {
    return window.matchMedia(DESKTOP_QUERY).matches;
  }

  /* ============================================================
     DESKTOP — Mega menú flyout
     ============================================================ */
  function initFlyout() {
    var flyout = document.getElementById('HeaderFlyout');
    if (!flyout) return;

    var backdrop = flyout.querySelector('[data-flyout-backdrop]');
    var closeBtn = flyout.querySelector('[data-flyout-close]');
    var categoryLinks = qsa('[data-flyout-category-link]', flyout);
    var panelGroups = qsa('[data-flyout-panel]', flyout);
    var triggers = qsa('[data-flyout-open]');

    var activeTrigger = null;
    var openTimer = null;
    var closeTimer = null;
    var isOpen = false;

    function setActiveCategory(handle) {
      categoryLinks.forEach(function (a) {
        a.classList.toggle('is-active', a.getAttribute('data-category') === handle);
      });
      panelGroups.forEach(function (p) {
        if (p.getAttribute('data-flyout-panel') === handle) {
          p.removeAttribute('hidden');
        } else {
          p.setAttribute('hidden', '');
        }
      });
    }

    function lockScroll() {
      var scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      if (scrollbarWidth > 0) {
        document.documentElement.style.paddingRight = scrollbarWidth + 'px';
      }
      document.documentElement.classList.add('kk-flyout-lock');
    }
    function unlockScroll() {
      document.documentElement.classList.remove('kk-flyout-lock');
      document.documentElement.style.paddingRight = '';
    }

    function positionPanel() {
      var header = document.getElementById('SiteHeader');
      var height = header ? header.getBoundingClientRect().bottom : 0;
      flyout.style.setProperty('--kk-header-height', Math.max(height, 0) + 'px');
    }

    function open(handle, trigger) {
      if (!isDesktop()) return;
      clearTimeout(closeTimer);
      positionPanel();
      setActiveCategory(handle);

      if (!isOpen) {
        flyout.hidden = false;
        requestAnimationFrame(function () {
          flyout.classList.add('is-open');
        });
        lockScroll();
        document.addEventListener('keydown', onKeydown, true);
      }

      isOpen = true;
      if (trigger) {
        activeTrigger = trigger;
        trigger.setAttribute('aria-expanded', 'true');
      }
    }

    function close(opts) {
      opts = opts || {};
      if (!isOpen) return;
      isOpen = false;
      flyout.classList.remove('is-open');
      unlockScroll();
      document.removeEventListener('keydown', onKeydown, true);
      triggers.forEach(function (t) {
        t.setAttribute('aria-expanded', 'false');
      });
      setTimeout(function () {
        if (!isOpen) flyout.hidden = true;
      }, 250);
      if (opts.returnFocus !== false && activeTrigger) {
        activeTrigger.focus();
      }
      activeTrigger = null;
    }

    function scheduleClose() {
      clearTimeout(closeTimer);
      closeTimer = setTimeout(function () {
        close();
      }, 180);
    }
    function cancelScheduledClose() {
      clearTimeout(closeTimer);
    }

    function getFocusable() {
      return qsa('a[href], button:not([disabled])', flyout).filter(function (el) {
        return el.offsetParent !== null;
      });
    }

    function onKeydown(e) {
      if (e.key === 'Escape' || e.keyCode === 27) {
        close();
        return;
      }
      if (e.key === 'Tab' || e.keyCode === 9) {
        var focusable = getFocusable();
        if (!focusable.length) return;
        var first = focusable[0];
        var last = focusable[focusable.length - 1];
        var current = document.activeElement;
        var currentIsInside = flyout.contains(current);

        if (!currentIsInside) {
          e.preventDefault();
          first.focus();
          return;
        }
        if (!e.shiftKey && current === last) {
          e.preventDefault();
          first.focus();
        } else if (e.shiftKey && current === first) {
          e.preventDefault();
          last.focus();
        }
      }
    }

    triggers.forEach(function (trigger) {
      var handle = trigger.getAttribute('data-category');

      trigger.addEventListener('mouseenter', function () {
        if (!isDesktop()) return;
        cancelScheduledClose();
        clearTimeout(openTimer);
        openTimer = setTimeout(function () {
          open(handle, trigger);
        }, 60);
      });
      trigger.addEventListener('mouseleave', function () {
        clearTimeout(openTimer);
        scheduleClose();
      });
      trigger.addEventListener('focus', function () {
        if (!isDesktop()) return;
        cancelScheduledClose();
        open(handle, trigger);
      });
    });

    flyout.addEventListener('mouseenter', cancelScheduledClose);
    flyout.addEventListener('mouseleave', scheduleClose);

    categoryLinks.forEach(function (a) {
      a.addEventListener('mouseenter', function () {
        cancelScheduledClose();
        setActiveCategory(a.getAttribute('data-category'));
      });
      a.addEventListener('focus', function () {
        setActiveCategory(a.getAttribute('data-category'));
      });
    });

    qsa('a', flyout).forEach(function (a) {
      a.addEventListener('click', function () {
        close({ returnFocus: false });
      });
    });

    if (closeBtn) closeBtn.addEventListener('click', function () { close(); });
    if (backdrop) backdrop.addEventListener('click', function () { close(); });

    document.addEventListener('click', function (e) {
      if (!isOpen) return;
      if (flyout.contains(e.target)) return;
      if (e.target.closest && e.target.closest('[data-flyout-open]')) return;
      close();
    });

    window.addEventListener('resize', function () {
      if (isOpen) positionPanel();
      if (!isDesktop() && isOpen) close({ returnFocus: false });
    });
  }

  /* ============================================================
     MOBILE — Drawer por niveles (dentro de #NavDrawer)
     ============================================================ */
  function initMobileNav() {
    var root = document.querySelector('[data-mnav]');
    if (!root) return;

    var levels = qsa('.kk-mnav__level', root);
    var levelsContainer = root.querySelector('[data-mnav-levels]');

    function showLevel(handle) {
      levels.forEach(function (level) {
        if (level.getAttribute('data-mnav-level') === handle) {
          level.removeAttribute('hidden');
        } else {
          level.setAttribute('hidden', '');
        }
      });
      if (levelsContainer) levelsContainer.scrollTop = 0;
    }

    function resetToRoot() {
      showLevel('root');
    }

    root.addEventListener('click', function (e) {
      var openBtn = e.target.closest('[data-mnav-open]');
      if (openBtn) {
        var handle = openBtn.getAttribute('data-mnav-open');
        showLevel(handle);
        var target = document.getElementById('MnavLevel-' + handle);
        var back = target && target.querySelector('[data-mnav-back]');
        if (back) back.focus();
        return;
      }
      var backBtn = e.target.closest('[data-mnav-back]');
      if (backBtn) {
        resetToRoot();
      }
    });

    qsa('.js-drawer-open-nav').forEach(function (btn) {
      btn.addEventListener('click', function () {
        resetToRoot();
      });
    });
  }

  function boot() {
    initFlyout();
    initMobileNav();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
